import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import { useMemo } from 'react'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { getWeekDateRange } from '../_utils/getWeekDateRange'
import { parseAllZoneEvents } from '../_utils/parseZoneEvents'
import { CommentTextarea } from '../CommentTextarea'
import type { TIcsDashboardData } from '../types'
import { computeZoneSummaries } from './_utils/computeZoneSummaries'
import { getWeekRangeTitle } from './_utils/getWeekRangeTitle'
import { PlannedVsAppliedTable } from './PlannedVsAppliedTable'

export const WeeklyIrrigationOverview = ({
  eventsResponse,
  resetKey,
}: {
  eventsResponse: TIcsDashboardData['eventsResponse']
  resetKey: number
}) => {
  const selectedPropertyId = selectedFieldAssetsStore.useSelector((s) => s.property)
  const selectedProperty = fieldAssetStore.useSelector((s) => s.properties)?.[Number(selectedPropertyId)]

  const summaries = useMemo(() => {
    if (!selectedProperty?.timezone) return null

    const { timezone } = selectedProperty
    const tz = moment.tz(timezone)
    const lastWeekDates = getWeekDateRange(tz, -1)
    const thisWeekDates = getWeekDateRange(tz, 0)
    const nextWeekDates = getWeekDateRange(tz, 1)
    const parsedEvents = parseAllZoneEvents(eventsResponse, timezone)

    return {
      twoWeeksAgo: computeZoneSummaries(eventsResponse, lastWeekDates, timezone, parsedEvents),
      lastWeek: computeZoneSummaries(eventsResponse, thisWeekDates, timezone, parsedEvents),
      nextWeek: computeZoneSummaries(eventsResponse, nextWeekDates, timezone, parsedEvents),
      twoWeeksAgoTitle: getWeekRangeTitle(-1, timezone),
      lastWeekTitle: getWeekRangeTitle(0, timezone),
      nextWeekTitle: getWeekRangeTitle(1, timezone),
    }
  }, [eventsResponse, selectedProperty])

  if (!summaries) return null

  return (
    <div>
      <h3 css={{ marginBottom: 40 }}>{translate.phrases.banyanApp('Planned vs Applied')}</h3>
      <div css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <PlannedVsAppliedTable zoneSummaries={summaries.twoWeeksAgo} title={summaries.twoWeeksAgoTitle} />
        <PlannedVsAppliedTable zoneSummaries={summaries.lastWeek} title={summaries.lastWeekTitle} />
        <PlannedVsAppliedTable
          zoneSummaries={summaries.nextWeek}
          title={summaries.nextWeekTitle}
          includeApplied={false}
        />
      </div>
      <CommentTextarea resetKey={resetKey} />
    </div>
  )
}
